import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveCategory, setActiveTab, pushNavEntry } from '../../redux/slices/appSlice';
import { requestShowPlaylist } from '../../utils/playlistOpenRequest';
import { clearResults as clearMixpediaResults } from '../../redux/slices/djpalSlice';
import { track } from '../../utils/analytics';
import MixGoatLogo from '../MixGoatLogo';
import { NAV_GROUPS } from './navItems';
import rekordboxLogo from '../../assets/rekordbox-logo.png';
import seratoLogo from '../../assets/serato-logo-10px.png';
import traktorLogo from '../../assets/traktor-logo.png';
import virtualdjLogo from '../../assets/virtual-dj-logo.png';

// DESIGN V2 — the left sidebar IS the navigation (one level, like Spotify's
// and Apple Music's): logo, the three product areas as labelled groups, the
// collection panels (Playlists / Liked / Artists), and the DJ-software
// panels pinned to the bottom. Replaces both the top category tabs and the
// 52px icon rail while the flag is on. Desktop only — the web navbar is
// untouched.
const PlaylistsIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 6H3" /><path d="M15 12H3" /><path d="M15 18H3" /><circle cx="19" cy="18" r="2" /><path d="M21 6V16" />
    </svg>
);
const HeartIcon = ({ filled }) => (
    <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
);
const ArtistsIcon = ({ filled }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <polygon points="18 12 19.8 15.5 23.5 16 21 18.5 21.5 22 18 20 14.5 22 15 18.5 12.5 16 16.2 15.5" fill={filled ? 'currentColor' : 'none'} strokeWidth="1.4" />
    </svg>
);

const SidebarIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="3" /><path d="M9 4v16" />
    </svg>
);
const Chevron = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
);
const readFlag = (k, d) => { try { const v = localStorage.getItem(k); return v == null ? d : v === '1'; } catch { return d; } };
const writeFlag = (k, v) => { try { localStorage.setItem(k, v ? '1' : '0'); } catch { /* ignore */ } };

// Collapsed = a 64px icon rail (Spotify's model: the user's choice, never
// hover-to-open). [data-nav="rail"] on <html> switches it in CSS, so the
// content column, search and panels follow it in the same frame. Below NARROW_PX the rail is the default; a toggle there opens it
// for this window size only, and widening the window restores the saved choice.
const NAV_COLLAPSED_KEY = 'mixgoat-v2-nav-collapsed';
const NARROW_PX = 1100;
const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || '');
const SHORTCUT = isMac ? '\u2318B' : 'Ctrl+B';

const DJ_APPS = [
    { key: 'rekordbox', label: 'Rekordbox', logo: rekordboxLogo },
    { key: 'serato', label: 'Serato', logo: seratoLogo },
    { key: 'traktor', label: 'Traktor', logo: traktorLogo },
    { key: 'virtualdj', label: 'Virtual DJ', logo: virtualdjLogo },
];

export default function SideNav({ panels, onOpenPanel, onClosePanels, djChangesPending = {}, showDjApps }) {
    const dispatch = useDispatch();
    const activeCategory = useSelector((s) => s.app.activeCategory);
    const activeTab = useSelector((s) => s.app.activeTab);
    const automixAvailable = useSelector((s) => !!s.automix?.engine?.available);
    // A DJ app is listed once it has synced at least once or has a library on
    // this computer; the others stay one click away under "Other DJ software".
    const djConnected = useSelector((s) => ({
        rekordbox: s.rekordbox?.syncStatus !== 'not_synced', serato: s.serato?.syncStatus !== 'not_synced',
        traktor: s.traktor?.syncStatus !== 'not_synced', virtualdj: s.virtualdj?.syncStatus !== 'not_synced',
    }), (a, b) => JSON.stringify(a) === JSON.stringify(b));
    const anyPanelOpen = Object.values(panels).some(Boolean);
    // Not remembered: the full list is for connecting a new app, a one-off, so
    // the sidebar comes back with only the user's own apps.
    const [djAllOpen, setDjAllOpen] = useState(false);
    const [pinnedCollapsed, setPinnedCollapsed] = useState(() => readFlag(NAV_COLLAPSED_KEY, false));
    const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < NARROW_PX);
    const [narrowOpen, setNarrowOpen] = useState(false);
    const rail = narrow ? !narrowOpen : pinnedCollapsed;
    const [tip, setTip] = useState(null);
    useEffect(() => {
        const mq = window.matchMedia(`(max-width: ${NARROW_PX - 1}px)`);
        const onChange = () => {
            if (mq.matches !== narrow) track('sidenav_toggled', { collapsed: mq.matches ? true : pinnedCollapsed, via: 'auto' });
            setNarrow(mq.matches); setNarrowOpen(false);
        };
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, [narrow, pinnedCollapsed]);
    // Layout effect: the attribute lands in the same frame as the is-rail class.
    useLayoutEffect(() => {
        const root = document.documentElement;
        if (rail) root.setAttribute('data-nav', 'rail'); else root.removeAttribute('data-nav');
        setTip(null);
        return () => root.removeAttribute('data-nav');
    }, [rail]);
    const toggleRail = useCallback((via) => {
        track('sidenav_toggled', { collapsed: !rail, via });
        if (narrow) setNarrowOpen(rail);
        else { setPinnedCollapsed(!rail); writeFlag(NAV_COLLAPSED_KEY, !rail); }
    }, [rail, narrow]);
    useEffect(() => {
        const onKey = (e) => {
            if ((isMac ? e.metaKey : e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === 'b' || e.key === 'B')) {
                e.preventDefault(); toggleRail('shortcut');
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [toggleRail]);
    // In the rail the names live in a tooltip beside each icon (fixed, so the
    // scroll region's overflow cannot clip it).
    const tipProps = (label) => (rail ? {
        'aria-label': label,
        onMouseEnter: (e) => { const r = e.currentTarget.getBoundingClientRect(); setTip({ label, top: r.top + r.height / 2 }); },
        onMouseLeave: () => setTip(null),
        onFocus: (e) => { const r = e.currentTarget.getBoundingClientRect(); setTip({ label, top: r.top + r.height / 2 }); },
        onBlur: () => setTip(null),
    } : {});
    // DJ apps with a library on this computer count as theirs before the first sync.
    const [djInstalled, setDjInstalled] = useState({});
    useEffect(() => {
        if (!showDjApps) return undefined;
        let live = true;
        window.electron?.djapp?.detectInstalled?.().then((r) => { if (live && r) setDjInstalled(r); }).catch(() => {});
        return () => { live = false; };
    }, [showDjApps]);
    const djMoreRef = useRef(null);
    const toggleDjAll = () => {
        track('sidenav_group_toggle', { group: 'dj', open: !djAllOpen });
        setDjAllOpen(!djAllOpen);
        // The full list can push its own toggle below the sidebar's (scrollbar-
        // less) edge; keep it in view so "Fewer" is always reachable.
        requestAnimationFrame(() => djMoreRef.current?.scrollIntoView({ block: 'nearest' }));
    };
    const openPanel = (key) => {
        // The Playlists item is the list, never the playlist you left open:
        // reopening lands on the list, and clicking it while inside a playlist
        // goes up to the list (as a history stop, so Back returns to the playlist).
        if (key === 'playlists') requestShowPlaylist(null);
        if (panels[key]) {
            if (key === 'playlists') dispatch(pushNavEntry({ category: activeCategory, tab: activeTab, panel: key }));
            return;
        }
        onOpenPanel(key);
        // The page goes into history on top of the tab it covers, so Back
        // returns to that tab and Forward brings the page back.
        dispatch(pushNavEntry({ category: activeCategory, tab: activeTab, panel: key }));
    };

    const go = (group, item) => {
        // A collection / DJ page is an overlay over the content column: leave it
        // when a page in the navigation is chosen.
        if (anyPanelOpen && onClosePanels) onClosePanels();
        // Mixpedia is the one page with its own inner navigation (home →
        // search → entity). Clicking it while it is already open goes home,
        // exactly what the navbar's home button did in the classic design.
        if (item.key === 'djpal' && activeTab === 'djpal' && activeCategory === item.cat) {
            track('navbar_home_click', { from: 'sidenav' });
            dispatch(clearMixpediaResults());
            window.dispatchEvent(new Event('navbar-search-clear'));
            return;
        }
        if (activeCategory !== item.cat) {
            track('category_view', { category: item.cat, from: 'sidenav' });
            dispatch(setActiveCategory(item.cat));
        }
        if (activeTab !== item.key) {
            track('feature_open', { feature: item.key, category: item.cat, from: 'sidenav' });
        }
        dispatch(setActiveTab(item.key));
    };

    return (
        <nav className={`v2-sidenav${rail ? ' is-rail' : ''}`} aria-label="Main">
            <div className="v2-sidenav-head">
                {/* One logo in both states, so collapsing only narrows the column: in the
                    rail it is the way back out and turns into the sidebar icon on hover. */}
                <button type="button" className="v2-sidenav-logo" onClick={() => (rail ? toggleRail('button') : go(NAV_GROUPS[0], NAV_GROUPS[0].items[0]))}
                    title={rail ? undefined : 'For You'} {...tipProps(`Open sidebar  ${SHORTCUT}`)}>
                    <span className="v2-sidenav-logo-goat"><MixGoatLogo width={36} height={36} /></span>
                    <span className="v2-sidenav-logo-open"><SidebarIcon /></span>
                    <span className="v2-sidenav-wordmark">Mix<em>Goat</em></span>
                </button>
                <button type="button" className="v2-sidenav-collapse" onClick={() => toggleRail('button')} title={`Collapse sidebar  ${SHORTCUT}`} aria-label="Collapse sidebar"
                    tabIndex={rail ? -1 : undefined} aria-hidden={rail || undefined}>
                    <SidebarIcon />
                </button>
            </div>
            <div className="v2-sidenav-scroll">
                {NAV_GROUPS.map((group) => {
                    const items = group.items.filter((it) => !it.needsAutomix || automixAvailable);
                    const isOn = (item) => !anyPanelOpen && activeCategory === item.cat && activeTab === item.key;
                    return (
                        <div className="v2-sidenav-group" key={group.id}>
                            <div className="v2-sidenav-label" aria-hidden={rail || undefined}><span>{group.label}</span></div>
                            {items.map((item) => {
                                const Icon = item.icon;
                                // A page is "current" only while no collection panel sits on top of it.
                                const on = isOn(item);
                                return (
                                    <button type="button" key={item.key} className={`v2-sidenav-item${on ? ' on' : ''}`} aria-current={on ? 'page' : undefined} onClick={() => go(group, item)} {...tipProps(item.title)}>
                                        <Icon className="v2-sidenav-icon" aria-hidden="true" focusable="false" />
                                        <span>{item.title}</span>
                                    </button>
                                );
                            })}
                            {group.id === 'collection' && (
                                <>
                                    <button type="button" className={`v2-sidenav-item${panels.playlists ? ' on' : ''}`} onClick={() => openPanel('playlists')} aria-current={panels.playlists ? 'page' : undefined} {...tipProps('Playlists')}>
                                        <span className="v2-sidenav-icon"><PlaylistsIcon /></span><span>Playlists</span>
                                    </button>
                                    <button type="button" className={`v2-sidenav-item${panels.likes ? ' on' : ''}`} onClick={() => openPanel('likes')} aria-current={panels.likes ? 'page' : undefined} {...tipProps('Liked tracks')}>
                                        <span className="v2-sidenav-icon"><HeartIcon filled={panels.likes} /></span><span>Liked tracks</span>
                                    </button>
                                    <button type="button" className={`v2-sidenav-item${panels.artists ? ' on' : ''}`} onClick={() => openPanel('artists')} aria-current={panels.artists ? 'page' : undefined} {...tipProps('Favourite artists')}>
                                        <span className="v2-sidenav-icon"><ArtistsIcon filled={panels.artists} /></span><span>Favourite artists</span>
                                    </button>
                                </>
                            )}
                        </div>
                    );
                })}
            {showDjApps && (() => {
                const own = DJ_APPS.filter((a) => djConnected[a.key] || djInstalled[a.key] || panels[a.key]);
                // Nothing synced or installed: Rekordbox, by far the most used, stands in; the rest wait under "Other DJ software".
                const connected = own.length ? own : DJ_APPS.filter((a) => a.key === 'rekordbox');
                const others = DJ_APPS.filter((a) => !connected.includes(a));
                const shown = djAllOpen && !rail ? DJ_APPS : connected;
                const djItem = (app) => (
                    <button type="button" key={app.key} className={`v2-sidenav-item${panels[app.key] ? ' on' : ''}${djConnected[app.key] ? '' : ' is-idle'}`} onClick={() => openPanel(app.key)} aria-current={panels[app.key] ? 'page' : undefined}
                        title={rail ? undefined : djChangesPending[app.key] ? `Updates from ${app.label} — ${djChangesPending[app.key].summary}` : (djConnected[app.key] ? undefined : `Connect ${app.label}`)}
                        {...tipProps(djConnected[app.key] ? app.label : `Connect ${app.label}`)}>
                        <span className="v2-sidenav-icon v2-mask" style={{ WebkitMaskImage: `url(${app.logo})`, maskImage: `url(${app.logo})` }} aria-hidden="true" />
                        <span>{app.label}</span>
                        {djChangesPending[app.key] && <i className="v2-sidenav-dot" aria-label="Changes pending" />}
                    </button>
                );
                return (
                    <div className="v2-sidenav-group v2-sidenav-dj">
                        <div className="v2-sidenav-label" aria-hidden={rail || undefined}><span>DJ software</span></div>
                        {shown.map(djItem)}
                        {others.length > 0 && (
                            <button type="button" ref={djMoreRef} className="v2-sidenav-item v2-sidenav-more" onClick={toggleDjAll} aria-expanded={djAllOpen}>
                                <span className={`v2-sidenav-icon v2-sidenav-chev${djAllOpen ? ' open' : ''}`}><Chevron /></span>
                                <span>{djAllOpen ? 'Fewer' : 'Other DJ software'}</span>
                            </button>
                        )}
                    </div>
                );
            })()}
            </div>
            {tip && <div className="v2-sidenav-tip" role="tooltip" style={{ top: tip.top }}>{tip.label}</div>}
        </nav>
    );
}
